import React, { useState } from "react";
import styled, { ThemeProvider } from "styled-components";
import { lightTheme } from "./Themes";
import { motion, AnimatePresence } from "framer-motion";

import LogoComponent from "../subComponents/LogoComponent";
import SocialIcons from "../subComponents/SocialIcons";
import PowerButton from "../subComponents/PowerButton";
import ParticleComponent from "../subComponents/ParticleComponent";
import BigTitle from "../subComponents/BigTitlte";
import SkillGameArcade from "../subComponents/SkillGameArcade";
import { device } from "../utils/breakpoints";

const PageContainer = styled.div`
  background-color: ${(props) => props.theme.body};
  width: 100%;
  min-height: 100vh;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-x: hidden;
  padding: 6rem 2rem 5rem;
  box-sizing: border-box;

  @media ${device.tablet} {
    padding: 5rem 1.25rem 4rem;
  }
  @media ${device.mobileL} {
    padding: 4.5rem 0.85rem 3.5rem;
  }
`;

const SocialIconsWrapper = styled.div`
  @media ${device.mobileL} {
    display: none;
  }
`;

const GameDashboard = styled(motion.div)`
  width: 100%;
  max-width: 1100px;
  z-index: 3;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

// Character Stat Banner
const CharacterBanner = styled.div`
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid #0f172a;
  border-radius: 20px;
  padding: 1.5rem 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 1.25rem;

  @media ${device.mobileL} {
    padding: 1.1rem;
    border-radius: 16px;
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
`;

const CharInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const CharName = styled.h1`
  font-family: "Karla", sans-serif;
  font-size: clamp(1.25rem, 3vw, 1.9rem);
  font-weight: 800;
  margin: 0;
  color: #0f172a;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;

  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const CharRank = styled.span`
  background: linear-gradient(135deg, #0f172a, #334155);
  color: #ffffff;
  font-size: 0.75rem;
  padding: 0.2rem 0.65rem;
  border-radius: 50px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
`;

const XPBarWrapper = styled.div`
  flex-grow: 1;
  max-width: 340px;
  width: 100%;

  @media ${device.mobileL} {
    max-width: 100%;
  }
`;

const XPLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  font-weight: 700;
  color: #334155;
  margin-bottom: 0.35rem;
`;

const XPBarBackground = styled.div`
  width: 100%;
  height: 12px;
  background: #e2e8f0;
  border-radius: 50px;
  overflow: hidden;
  border: 1px solid #cbd5e1;
`;

const XPBarFill = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, #38bdf8, #6366f1, #a855f7);
  border-radius: 50px;
`;

const ModeSwitchGroup = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    width: 100%;
    button {
      flex: 1;
      justify-content: center;
      padding: 0.6rem 0.5rem;
      font-size: 0.85rem;
    }
  }
`;

const ModeButton = styled(motion.button)`
  background: ${(props) =>
    props.active ? "linear-gradient(135deg, #0f172a, #1e293b)" : "transparent"};
  color: ${(props) => (props.active ? "#ffffff" : "#0f172a")};
  border: 2px solid #0f172a;
  padding: 0.65rem 1.25rem;
  border-radius: 50px;
  font-family: "Karla", sans-serif;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.45rem;
  box-shadow: ${(props) => (props.active ? "0 4px 15px rgba(15,23,42,0.25)" : "none")};

  &:hover {
    background: #0f172a;
    color: #ffffff;
  }
`;

// Horizontally scrollable tabs on small screens
const RealmTabsContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 0.3rem;

  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 10px;
  }
`;

const RealmTabs = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;

  @media ${device.mobileL} {
    justify-content: flex-start;
    width: max-content;
  }
`;

const RealmTab = styled.button`
  background: ${(props) => (props.active ? "#0f172a" : "rgba(255, 255, 255, 0.75)")};
  color: ${(props) => (props.active ? "#ffffff" : "#334155")};
  border: 1.5px solid ${(props) => (props.active ? "#0f172a" : "#cbd5e1")};
  padding: 0.45rem 1rem;
  border-radius: 30px;
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    border-color: #0f172a;
  }

  @media (max-width: 480px) {
    padding: 0.4rem 0.85rem;
    font-size: 0.8rem;
  }
`;

// Skill Nodes Grid
const SkillsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
  gap: 1.25rem;
  width: 100%;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const SkillCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.92);
  border: 2px solid #0f172a;
  border-radius: 16px;
  padding: 1.25rem;
  box-shadow: 0 5px 0 #0f172a;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: transform 0.15s ease;

  &:hover {
    transform: translateY(-3px);
  }

  @media (max-width: 480px) {
    padding: 1.1rem;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.7rem;
`;

const SkillTitle = styled.h3`
  font-family: "Karla", sans-serif;
  font-size: 1.15rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const LevelBadge = styled.span`
  background: ${(props) => props.bg || "#e0f2fe"};
  color: ${(props) => props.color || "#0369a1"};
  font-size: 0.75rem;
  font-weight: 800;
  padding: 0.2rem 0.6rem;
  border-radius: 20px;
  border: 1px solid ${(props) => props.color || "#0369a1"};
`;

const ProgressWrapper = styled.div`
  margin-bottom: 0.85rem;
`;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.78rem;
  font-weight: 700;
  color: #64748b;
  margin-bottom: 0.25rem;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #f1f5f9;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid #cbd5e1;
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  background: ${(props) => props.color || "#0ea5e9"};
  border-radius: 20px;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
`;

const SkillTag = styled.span`
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  color: #334155;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.18rem 0.5rem;
  border-radius: 6px;
`;

const playClickChime = () => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(523.25, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1046.5, ctx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.12, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.15);
  } catch (e) {}
};

// Realistic percentages tailored for 2.5 Years of Experience (~75% - 86%)
const SKILL_NODES = [
  {
    id: "react",
    realm: "frontend",
    icon: "⚛️",
    name: "React.js & Redux",
    level: "Lv. 86",
    tier: "Advanced",
    percentage: 86,
    color: "#0ea5e9",
    bg: "#e0f2fe",
    tags: ["Hooks", "Redux Toolkit", "Next.js", "Virtual DOM", "SPA"],
  },
  {
    id: "ts",
    realm: "frontend",
    icon: "📘",
    name: "TypeScript & JS",
    level: "Lv. 84",
    tier: "Advanced",
    percentage: 84,
    color: "#3b82f6",
    bg: "#dbeafe",
    tags: ["ES6+", "Static Typing", "Generics", "Async/Await"],
  },
  {
    id: "css",
    realm: "frontend",
    icon: "🎨",
    name: "Modern UI & Tailwind",
    level: "Lv. 85",
    tier: "Advanced",
    percentage: 85,
    color: "#06b6d4",
    bg: "#cffafe",
    tags: ["Tailwind CSS", "Styled-Components", "Glassmorphism", "Responsive"],
  },
  {
    id: "node",
    realm: "backend",
    icon: "🟢",
    name: "Node.js & Express",
    level: "Lv. 82",
    tier: "Advanced",
    percentage: 82,
    color: "#10b981",
    bg: "#d1fae5",
    tags: ["RESTful APIs", "WebSockets", "JWT Auth", "MVC Pattern"],
  },
  {
    id: "solana",
    realm: "blockchain",
    icon: "◎",
    name: "Solana & Web3",
    level: "Lv. 82",
    tier: "Advanced",
    percentage: 82,
    color: "#8b5cf6",
    bg: "#ede9fe",
    tags: ["SPL Tokens", "Web3 Wallets", "RPC APIs", "On-chain Logic"],
  },
  {
    id: "db",
    realm: "backend",
    icon: "🗄️",
    name: "Databases (SQL/NoSQL)",
    level: "Lv. 80",
    tier: "Proficient",
    percentage: 80,
    color: "#84cc16",
    bg: "#ecfccb",
    tags: ["MongoDB", "PostgreSQL", "MySQL", "Indexing", "Mongoose"],
  },
  {
    id: "tools",
    realm: "tools",
    icon: "🛠️",
    name: "DevOps & Tooling",
    level: "Lv. 78",
    tier: "Proficient",
    percentage: 78,
    color: "#f59e0b",
    bg: "#fef3c7",
    tags: ["Git & GitHub", "Linux CLI", "Postman", "Nginx", "PM2"],
  },
  {
    id: "python",
    realm: "backend",
    icon: "🐍",
    name: "Python & Scripts",
    level: "Lv. 75",
    tier: "Proficient",
    percentage: 75,
    color: "#eab308",
    bg: "#fef9c3",
    tags: ["Automation", "Data Processing", "Scripting", "APIs"],
  },
];

const MySkillsPage = () => {
  const [activeMode, setActiveMode] = useState("TREE");
  const [activeRealm, setActiveRealm] = useState("ALL");
  const [bonusXP, setBonusXP] = useState(8250);

  const handleNodeClick = () => {
    playClickChime();
    setBonusXP((prev) => Math.min(10000, prev + 25));
  };

  const filteredSkills =
    activeRealm === "ALL"
      ? SKILL_NODES
      : SKILL_NODES.filter((item) => item.realm === activeRealm.toLowerCase());

  return (
    <ThemeProvider theme={lightTheme}>
      <PageContainer>
        <LogoComponent theme="light" />
        <SocialIconsWrapper>
          <SocialIcons theme="light" />
        </SocialIconsWrapper>
        <PowerButton />
        <ParticleComponent theme="light" />

        <GameDashboard
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Character Stats Header */}
          <CharacterBanner>
            <CharInfo>
              <CharName>
                ⚡ Gourav Mishra <CharRank>2.5 YOE · Full Stack Engineer</CharRank>
              </CharName>
              <span style={{ color: "#64748b", fontSize: "0.85rem", fontWeight: 600 }}>
                Full Stack (MERN) & Blockchain Architecture Specialist
              </span>
            </CharInfo>

            <XPBarWrapper>
              <XPLabel>
                <span>Total Experience Points</span>
                <span>{bonusXP} / 10,000 XP</span>
              </XPLabel>
              <XPBarBackground>
                <XPBarFill
                  initial={{ width: 0 }}
                  animate={{ width: `${(bonusXP / 10000) * 100}%` }}
                  transition={{ duration: 0.8, type: "spring" }}
                />
              </XPBarBackground>
            </XPBarWrapper>

            <ModeSwitchGroup>
              <ModeButton
                active={activeMode === "TREE"}
                onClick={() => setActiveMode("TREE")}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                📜 Skill Tree
              </ModeButton>
              <ModeButton
                active={activeMode === "ARCADE"}
                onClick={() => setActiveMode("ARCADE")}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                🕹️ Play Arcade
              </ModeButton>
            </ModeSwitchGroup>
          </CharacterBanner>

          {/* Interactive RPG Skill Tree View */}
          <AnimatePresence mode="wait">
            {activeMode === "TREE" ? (
              <motion.div
                key="tree"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}
              >
                <RealmTabsContainer>
                  <RealmTabs>
                    <RealmTab active={activeRealm === "ALL"} onClick={() => setActiveRealm("ALL")}>
                      ⚡ All Realms
                    </RealmTab>
                    <RealmTab
                      active={activeRealm === "FRONTEND"}
                      onClick={() => setActiveRealm("FRONTEND")}
                    >
                      ⚛️ Frontend Arena
                    </RealmTab>
                    <RealmTab
                      active={activeRealm === "BACKEND"}
                      onClick={() => setActiveRealm("BACKEND")}
                    >
                      🛠️ Backend Dungeon
                    </RealmTab>
                    <RealmTab
                      active={activeRealm === "BLOCKCHAIN"}
                      onClick={() => setActiveRealm("BLOCKCHAIN")}
                    >
                      ⛓️ Blockchain Realm
                    </RealmTab>
                    <RealmTab
                      active={activeRealm === "TOOLS"}
                      onClick={() => setActiveRealm("TOOLS")}
                    >
                      🧰 Dev Armor
                    </RealmTab>
                  </RealmTabs>
                </RealmTabsContainer>

                <SkillsGrid layout>
                  {filteredSkills.map((skill) => (
                    <SkillCard
                      key={skill.id}
                      layout
                      onClick={handleNodeClick}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      whileActive={{ scale: 0.97 }}
                    >
                      <div>
                        <CardHeader>
                          <SkillTitle>
                            <span>{skill.icon}</span> {skill.name}
                          </SkillTitle>
                          <LevelBadge bg={skill.bg} color={skill.color}>
                            {skill.level}
                          </LevelBadge>
                        </CardHeader>

                        <ProgressWrapper>
                          <ProgressLabel>
                            <span>{skill.tier} Tier</span>
                            <span>{skill.percentage}% Mastery</span>
                          </ProgressLabel>
                          <ProgressBar>
                            <ProgressFill
                              color={skill.color}
                              initial={{ width: 0 }}
                              animate={{ width: `${skill.percentage}%` }}
                              transition={{ duration: 1 }}
                            />
                          </ProgressBar>
                        </ProgressWrapper>
                      </div>

                      <TagsContainer>
                        {skill.tags.map((tag, idx) => (
                          <SkillTag key={idx}>+{tag}</SkillTag>
                        ))}
                      </TagsContainer>
                    </SkillCard>
                  ))}
                </SkillsGrid>
              </motion.div>
            ) : (
              <motion.div
                key="arcade"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
              >
                <SkillGameArcade onExit={() => setActiveMode("TREE")} />
              </motion.div>
            )}
          </AnimatePresence>
        </GameDashboard>

        <BigTitle text="SKILLS" top="80%" right="25%" />
      </PageContainer>
    </ThemeProvider>
  );
};

export default MySkillsPage;
